import { FC } from "react";
import { Box } from "@mui/material";
import * as Flags from "country-flag-icons/react/3x2";
import { findCountryByName } from "../../utils/countries";

type Props = {
  /** The stored country name, e.g. "Denmark". Anything unrecognised renders nothing. */
  countryName?: string | null;
  width?: number;
};

/**
 * The flag for a stored country name. SVG rather than an emoji flag, because Windows has no flag
 * glyphs and would show a boxed "DK" instead.
 *
 * Renders nothing when the name does not resolve, so a record written before the picker existed
 * degrades to just its text rather than to a broken image.
 */
export const CountryFlag: FC<Props> = ({ countryName, width = 22 }) => {
  const country = findCountryByName(countryName);

  const Flag = country
    ? (Flags as Record<string, FC<{ title?: string }> | undefined>)[
        country.code
      ]
    : undefined;

  if (!Flag) {
    return null;
  }

  return (
    <Box
      component="span"
      sx={{
        width,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        lineHeight: 0
      }}
    >
      <Flag title={country?.name} />
    </Box>
  );
};

/** Flag by ISO code, for the picker where the option is already resolved. */
export const CountryFlagByCode: FC<{ code: string; width?: number }> = ({
  code,
  width = 22
}) => {
  const Flag = (Flags as Record<string, FC<{ title?: string }> | undefined>)[
    code
  ];

  return (
    <Box
      component="span"
      sx={{
        width,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        lineHeight: 0
      }}
    >
      {Flag ? <Flag /> : null}
    </Box>
  );
};
